import type { Board, BoardProject, BoardTask } from "./api.ts";

/**
 * The editor's board, sorted into what to do next.
 *
 * The server sends two flat lists: the tasks worth doing, and the projects made to answer them.
 * Which pile each one belongs in is a rule, not a layout, and it lives apart from the page for
 * that reason. It is the one decision on this screen worth testing without a DOM, and "whose work
 * is this" is subtle enough to deserve tests of its own: a project somebody else started, whose
 * latest version you uploaded, is yours to act on, and the reverse is not.
 *
 * Order is the server's. It sorts both lists by when they were made, and rows keep that order
 * inside a group; only the groups themselves come in a fixed order, the ones that want this
 * reader first.
 */

export type BoardGroupKey =
  "NEEDS_YOU" | "AWAITING" | "IN_PROGRESS" | "OTHERS" | "NOT_STARTED" | "DONE" | "DROPPED";

export type BoardRow =
  | { kind: "TASK"; task: BoardTask }
  | { kind: "PROJECT"; project: BoardProject; task: BoardTask | null };

export type BoardGroup = { key: BoardGroupKey; rows: BoardRow[] };

const ORDER: BoardGroupKey[] = [
  "NEEDS_YOU",
  "AWAITING",
  "IN_PROGRESS",
  "OTHERS",
  "NOT_STARTED",
  "DONE",
  "DROPPED",
];

/**
 * Whose work a project is: whoever made its current version, or, until there is one, whoever
 * started it.
 */
export function isMine(project: BoardProject, meId: string): boolean {
  return project.current ? project.current.createdBy === meId : project.createdBy === meId;
}

function groupOf(project: BoardProject, meId: string): BoardGroupKey {
  if (project.status === "APPROVED" || project.status === "DELIVERED") return "DONE";
  if (project.status === "ABANDONED") return "DROPPED";
  if (!isMine(project, meId)) return "OTHERS";
  if (project.status === "CHANGES_REQUESTED") return "NEEDS_YOU";
  if (project.status === "IN_REVIEW") return "AWAITING";
  if (project.status === "PLANNED" || project.status === "EDITING") return "IN_PROGRESS";
  // An unrecognised status on your own work is still your work in progress; "other editors" would
  // say something false about whose it is.
  return "IN_PROGRESS";
}

export function groupBoard(board: Board, meId: string): BoardGroup[] {
  const rows = new Map<BoardGroupKey, BoardRow[]>(ORDER.map((key) => [key, []]));
  // A project can name a task this reader cannot see (its brief went private); no match, no task.
  const taskById = new Map(board.tasks.map((t) => [t.tagId, t]));

  for (const project of board.projects) {
    const task = project.tagId ? (taskById.get(project.tagId) ?? null) : null;
    rows.get(groupOf(project, meId))!.push({ kind: "PROJECT", project, task });
  }
  // A task with a live project is already on the board as that project.
  for (const task of board.tasks) {
    if (task.projectId === null) rows.get("NOT_STARTED")!.push({ kind: "TASK", task });
  }

  return ORDER.map((key) => ({ key, rows: rows.get(key)! })).filter((g) => g.rows.length > 0);
}
